// features/serviceRequests/hooks.ts
import { consumerApi } from "@features/cx/consumerManagement/api";
import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommunicationFilters,
  CommunicationPayload,
} from "../shared/types/communicationTypes";
import { CreateNoteInput } from "../shared/types/notesTabProps";
import { ServiceOrderFilters } from "../shared/types/serviceOrdersTabProps";
import { serviceRequestApi } from "./api";
import {
  NoteFilters,
  ServiceRequestFilters,
  UtilityConfigFilters,
} from "./types";
import { downloadCSV } from "@shared/utils/utils";
import { toast } from "@shared/ui/use-toast";

// ================================
// CONSUMER SEARCH HOOKS
// ================================

/**
 * Search consumers for service requests
 */
export const useConsumerSearch = (params: {
  remoteUtilityId: number;
  requestType: string;
  searchData: string;
}) => {
  const enabled =
    !!params.remoteUtilityId &&
    !!params.requestType &&
    params.searchData.length >= 3;

  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.consumerSearch(params),
    () => serviceRequestApi.searchConsumer(params),
    {
      enabled,
      staleTime: 5 * 60 * 1000,
      retry: false,
    }
  );
};

/**
 * Get consumer meta data
 */
export const useConsumerMetaData = (params: {
  consumerId: number;
  remoteUtilityId: number;
}) => {
  const enabled = !!params.consumerId && !!params.remoteUtilityId;

  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.consumerMetaData(
      String(params.consumerId)
    ),
    () => serviceRequestApi.getConsumerMetaData(params),
    {
      enabled,
      staleTime: 5 * 60 * 1000,
    }
  );
};

// ================================
// CONFIGURATION HOOKS
// ================================

/**
 * Get utility request configurations
 */
export const useUtilityRequestConfiguration = (
  filters: UtilityConfigFilters
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.utilityRequestConfiguration(
      filters
    ),
    () => serviceRequestApi.getUtilityRequestConfiguration(filters),
    {
      enabled: !!filters.remoteUtilityId,
      staleTime: 10 * 60 * 1000,
    }
  );
};

// ================================
// CHOICES HOOKS
// ================================

/**
 * Get time slot choices
 */
export const useTimeSlotChoices = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.timeSlotChoices(),
    () => serviceRequestApi.getTimeSlotChoices(),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

/**
 * Get transaction status choices
 */
export const useTransactionStatusChoices = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.transactionStatusChoices(),
    () => serviceRequestApi.getTransactionStatusChoices(),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

/**
 * Get note type choices
 */
export const useNoteTypeChoices = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.noteTypeChoices(),
    () => serviceRequestApi.getNoteTypeChoices(),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

/**
 * Get service requests list
 */
export const useServiceRequests = (filters: ServiceRequestFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.list(filters),
    () => serviceRequestApi.getServiceRequests(filters),
    {
      enabled: !!filters.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

export const useServiceRequestsExport = (filters: ServiceRequestFilters = {}) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.exportList(filters), // Adjust this query key based on your factory structure
    async () => {
      setIsExporting(true);
      try {
        const blob = await serviceRequestApi.exportServiceRequests(filters); // Adjust API object name if needed
        
        toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });
        
        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to export service requests: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    {
      enabled: false,
    }
  );

  const exportCSV = () => {
    exportMutation.refetch();
  };

  return {
    exportCSV,
    isExporting: isExporting || exportMutation.isFetching,
    error: exportMutation.error,
    isError: exportMutation.isError,
  };
};

/**
 * Get service request detail
 */
export const useServiceRequestDetail = (requestId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.details(String(requestId)),
    () => serviceRequestApi.getServiceRequestDetail(requestId),
    {
      enabled: !!requestId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

/**
 * Get service request summary
 */
export const useServiceRequestSummary = (params: {
  remoteUtilityId: number;
  requestType: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.summary(params),
    () => serviceRequestApi.getServiceRequestSummary(params),
    {
      enabled: !!params.remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

// ================================
// SERVICE REQUEST MUTATION HOOKS
// ================================

/**
 * Create service request
 */
export const useCreateServiceRequest = () => {
  const navigate = useNavigate();
  return useSmartMutation(
    (payload: {
      consumerId: string;
      remoteUtilityId: string;
      utilityServices: any;
      serviceCharges: any;
      serviceName: any;
      data: any;
      requestDescription: any;
    }) =>
      consumerApi.createServiceRequest(
        payload.consumerId,
        payload.remoteUtilityId,
        payload.data
      ),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.serviceRequests.list(),
        QueryKeyFactory.module.cx.serviceRequests.summary(),
      ],
      onSuccess: (data, variables) => {
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceRequests
            .list()
            .slice(0, 3),
          exact: false,
        });
        // toast.success('Service request created successfully!');
        navigate("/cx/services/success", {
          state: {
            ...data,
            formData: variables.data,
            serviceCharges: variables.serviceCharges,
            utilityServices: variables.utilityServices,
            serviceName: variables.serviceName,
            requestDescription: variables.requestDescription,
          },
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Update service request
 */
export const useUpdateServiceRequest = (requestId: number) => {
  return useSmartMutation(
    (payload: any) =>
      serviceRequestApi.updateServiceRequest(requestId, payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.serviceRequests.details(String(requestId)),
        QueryKeyFactory.module.cx.serviceRequests.list(),
        QueryKeyFactory.module.cx.serviceRequests.summary(),
      ],
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.serviceRequests.details(
            String(requestId)
          ),
        });

        await queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.serviceRequests
            .list()
            .slice(0, 3),
          exact: false,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    }
  );
};

// ================================
// SERVICE REQUEST NOTES HOOKS
// ================================

/**
 * Get service request notes
 */
export const useServiceRequestNotes = (filters: NoteFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.notes(filters),
    () => serviceRequestApi.getServiceRequestNotes(filters),
    {
      enabled: !!filters.requestId,
      staleTime: 1 * 60 * 1000,
    }
  );
};

/**
 * Create service request note
 */
export const useCreateServiceRequestNote = (requestId: string) => {
  return useSmartMutation(
    (payload: CreateNoteInput) =>
      serviceRequestApi.createServiceRequestNote(payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.serviceRequests.notes(requestId),
        QueryKeyFactory.module.cx.serviceRequests.details(requestId),
      ],
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Note added successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: `Failed to add note: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

// ================================
// CUSTOM HOOKS FOR COMPLEX FLOWS
// ================================

/**
 * Custom hook for managing service request filters and pagination
 */
export const useServiceRequestFilters = (
  initialFilters: ServiceRequestFilters
) => {
  const [filters, setFilters] = useState<ServiceRequestFilters>({
    page: 1,
    limit: 50,
    ...initialFilters,
  });

  const updateFilters = useCallback(
    (newFilters: Partial<ServiceRequestFilters>) => {
      setFilters((prev) => ({
        ...prev,
        ...newFilters,
        page: newFilters.page || 1,
      }));
    },
    []
  );

  const resetFilters = useCallback(() => {
    setFilters({
      page: 1,
      limit: 50,
      ...initialFilters,
    });
  }, [initialFilters]);

  const nextPage = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      page: (prev.page || 1) + 1,
    }));
  }, []);

  const prevPage = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      page: Math.max((prev.page || 1) - 1, 1),
    }));
  }, []);

  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({
      ...prev,
      page: Math.max(page, 1),
    }));
  }, []);

  const queryResult = useServiceRequests(filters);

  return {
    filters,
    updateFilters,
    resetFilters,
    nextPage,
    prevPage,
    setPage,
    ...queryResult,
  };
};

/**
 * Custom hook for bulk service request operations
 */
export const useBulkServiceRequestSelection = () => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const toggleSelection = useCallback((id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id]
    );
  }, []);

  const selectAll = useCallback((ids: number[]) => {
    setSelectedIds(ids);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const isSelected = useCallback(
    (id: number) => {
      return selectedIds.includes(id);
    },
    [selectedIds]
  );

  return {
    selectedIds,
    toggleSelection,
    selectAll,
    clearSelection,
    isSelected,
    hasSelection: selectedIds.length > 0,
    selectionCount: selectedIds.length,
  };
};

export const useServiceStatus = (params: { remoteUtilityId: number; tab: number }) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.status({ tab: params.tab }), // Include params in query key for caching
    () => serviceRequestApi.getStatus(params)
  );
};

// Hook for setup progress (original)
export const useServiceCategory = (params: { remoteUtilityId: number }) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.category(), // Include params in query key for caching
    () => serviceRequestApi.getCategory(params)
  );
};

export const usePaymentMethod = (params: { remoteUtilityId: number }) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.paymentMethod(params), // Include params in query key for caching
    () => serviceRequestApi.getPaymentMethod(params),
    {
      enabled: !!params.remoteUtilityId,
      staleTime: 10 * 60 * 1000,
    }
  );
};

/**
 * Get communication history with filters
 */
export const useCommunicationHistory = (filters: CommunicationFilters = {}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.communications(
      String(filters.entityId)
    ),
    () => serviceRequestApi.getCommunications(filters),
    {
      enabled: !!filters.entityId,
      staleTime: 1 * 60 * 1000,
    }
  );
};

/**
 * Send communication
 */
export const useSendCommunication = (id: string) => {
  return useSmartMutation(
    (data: CommunicationPayload) => serviceRequestApi.sendCommunication(data),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.serviceRequests.communications(id),
        QueryKeyFactory.module.cx.serviceRequests.activityLogs(id),
      ],
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Communication sent successfully!",
          variant: "default",
        });
      },
    }
  );
};

export const useChannelType = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.channelType(),
    () => serviceRequestApi.getChannelType(),
    {
      enabled: true,
    }
  );
};

export const useChannelTemplates = (
  channelType: string,
  remoteUtilityId: number
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.serviceRequests.channelTemplates(
      remoteUtilityId,
      channelType
    ),
    () => serviceRequestApi.getChannelTemplates(channelType, remoteUtilityId),
    {
      enabled: !!channelType,
    }
  );
};

/**
 * Get service orders with filters
 */
export const useServiceOrders = (
  id: string,
  filters: ServiceOrderFilters = {}
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerServiceOrders(id),
    () => consumerApi.getServiceOrders(id, filters),
    {
      enabled: !!id,
      staleTime: 2 * 60 * 1000,
    }
  );
};
